import * as d3 from "d3";
import "../Charts.css"
import * as cc from '../ChartConstants';
import { GenericChartWithLegend } from "./GenericChartWithLegend";

const DEFAULT_PARAMETERS = {
    [cc.NORMAL_WIDTH]: 2.5,
    [cc.ENFASIS_WIDTH]: 7.5,
    'titleFontSize': '16px',
    'axisFontSize': '12px',
    'showLabels': true,
    'showXAxisLabels': true,
}

/**
 * Month abbreviation formatter (Spanish)
 */
const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun",
    "jul", "ago", "sep", "oct", "nov", "dic"];

function monthFormatter(date) {
    if (!date) return "";
    return MONTHS[new Date(date).getMonth()];
}

export class MultiLineChart extends GenericChartWithLegend {

    constructor(parameters, objectReference) {
        super({ ...DEFAULT_PARAMETERS, ...parameters }, objectReference)

        this.selectedData = null;
        this.selectedFlowId = -1;
        this.num_lines = 0
    }

    /**
     * Adaptive tick count based on data size
     */
    getAdaptiveTickCount(dataLength = 0) {
        if (dataLength <= 30) return dataLength;
        if (dataLength <= 90) return 12;
        return 8;
    }

    build(initialData) {
        super.build(initialData)
        super.updateData(initialData)

        let self = this;

        this.num_lines = initialData.length;
        this.colorScheme = this.getColorScheme(this.num_lines)

        // Scales
        this.xScale = d3.scaleTime()
            .domain([
                d3.min(initialData.map(ob => d3.min(ob[cc.X_VALUES]))),
                d3.max(initialData.map(ob => d3.max(ob[cc.X_VALUES])))])

            .range([0, this.width])

        this.min_left_y = d3.min(initialData.map(ob => d3.min(ob[cc.Y_VALUES])))
        this.max_left_y = d3.max(initialData.map(ob => d3.max(ob[cc.Y_VALUES])))

        this.final_left_padding =
            (this.max_left_y - this.min_left_y) *
            this.parameters[cc.Y_PADDING]

        this.yScale = d3.scaleLinear()
            .domain([
                this.min_left_y - this.final_left_padding,
                this.max_left_y + this.final_left_padding
            ])
            .range([this.height, 0])

        // =========================
        // X Axis (Month abbreviations)
        // =========================

        const tickCount = this.getAdaptiveTickCount(
            initialData[0]?.[cc.X_VALUES]?.length || 0
        )

        const xAxisGenerator = d3.axisBottom(this.xScale)
            .ticks(tickCount)
            .tickFormat(monthFormatter)

        if (!this.parameters['showXAxisLabels']) {
            xAxisGenerator.tickFormat("")
        }

        const xAxis = this.mainComponent.append('g')
            .attr('id', 'xAxis')
            .attr('class', 'axis axis--x')
            .attr('transform', 'translate(0,' + this.height + ')')
            .call(xAxisGenerator)

        xAxis.selectAll("text")
            .style("font-size", this.parameters['axisFontSize'])

        // =========================
        // Y Axis
        // =========================

        const yAxisGenerator = this.parameters[cc.Y_AXIS_ON_LEFT]
            ? d3.axisLeft(this.yScale)
            : d3.axisRight(this.yScale)

        const yAxis = this.mainComponent.append('g')
            .attr('id', 'yAxis')
            .attr('class', 'axis axis--y')
            .attr("transform",
                this.parameters[cc.Y_AXIS_ON_LEFT]
                    ? null
                    : `translate(${this.width},0)`
            )
            .call(yAxisGenerator)

        yAxis.selectAll("text")
            .style("font-size", this.parameters['axisFontSize'])

        if (this.parameters['titleFontSize']) {
            this.svgEl.select('.chart-title')
                .style('font-size', this.parameters['titleFontSize']);
        }

        this.mainComponent.selectAll(".constructedElement").remove()

        // =========================
        // Lines
        // =========================

        initialData.forEach((ob, i) => {

            this.mainComponent.append("path")
                .datum(ob[cc.X_VALUES])
                .attr('id', `line-${i}`)
                .attr('class', 'line constructedElement')
                .attr("fill", "none")
                .attr("stroke", this.colorScheme(i))
                .attr("stroke-width", this.parameters[cc.NORMAL_WIDTH])
                .attr("d", d3.line()
                    .x(function (d, idx) {
                        return self.xScale(ob[cc.X_VALUES][idx])
                    })
                    .y(function (d, idx) {
                        return self.yScale(ob[cc.Y_VALUES][idx])
                    })
                )

        })
    }

    updateData(newData) {

        super.updateData(newData)

        let self = this;

        this.xScale.domain([
            d3.min(newData.map(ob => d3.min(ob[cc.X_VALUES]))),
            d3.max(newData.map(ob => d3.max(ob[cc.X_VALUES])))
        ])

        this.yScale.domain([
            d3.min(newData.map(ob => d3.min(ob[cc.Y_VALUES]))) - this.final_left_padding,
            d3.max(newData.map(ob => d3.max(ob[cc.Y_VALUES]))) + this.final_left_padding
        ])

        const tickCount = this.getAdaptiveTickCount(
            newData[0]?.[cc.X_VALUES]?.length || 0
        )

        const xAxisGenerator = d3.axisBottom(this.xScale)
            .ticks(tickCount)
            .tickFormat(monthFormatter)

        if (!this.parameters['showXAxisLabels']) {
            xAxisGenerator.tickFormat("")
        }

        this.mainComponent.select("#xAxis")
            .transition()
            .duration(this.parameters[cc.ANIMATION_TIME])
            .call(xAxisGenerator)
            .selectAll("text")
            .style("font-size", this.parameters['axisFontSize'])

        const yAxisGenerator = this.parameters[cc.Y_AXIS_ON_LEFT]
            ? d3.axisLeft(this.yScale)
            : d3.axisRight(this.yScale)

        this.mainComponent.select("#yAxis")
            .transition()
            .duration(this.parameters[cc.ANIMATION_TIME])
            .call(yAxisGenerator)
            .selectAll("text")
            .style("font-size", this.parameters['axisFontSize'])

        newData.forEach((ob, i) => {

            this.mainComponent.select(`#line-${i}`)
                .datum(ob[cc.X_VALUES])
                .transition()
                .duration(this.parameters[cc.DATA_CHANGE_TRANSITION_TIME])
                .attr("d", d3.line()
                    .x(function (d, idx) {
                        return self.xScale(ob[cc.X_VALUES][idx])
                    })
                    .y(function (d, idx) {
                        return self.yScale(ob[cc.Y_VALUES][idx])
                    })
                )
        })
    }
}